import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Mail, Lock } from 'lucide-react';
import AuthShell from '../components/layout/AuthShell';
import Input from '../components/ui/Input';
import Button from '../components/ui/Button';
import SocialLogins from '../components/auth/SocialLogins';
import { useAuth, getErrorMessage } from '../context/AuthContext';
import { useToast } from '../components/ui/Toast';

export default function Login() {
  const { login, googleLogin } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/app';
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const err = searchParams.get('error');
    if (err) {
      toast.error(err === 'oauth_state' ? 'Sign-in expired — please try again.' : 'Social sign-in failed. Try again.');
      searchParams.delete('error');
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState({ email: '', password: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) e.email = 'Enter a valid email.';
    if (form.password.length < 1) e.password = 'Password is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await login(form.email, form.password);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (err) {
      const code = err?.response?.data?.error?.code;
      const details = err?.response?.data?.error?.details || {};
      if (code === 'EMAIL_UNVERIFIED') {
        toast.info('Please verify your email to continue.');
        navigate('/verify-email', { state: { email: details.email || form.email, devCode: details.devCode } });
        return;
      }
      if (code === 'USE_GOOGLE') {
        toast.error('This account uses Google sign-in — use the button below.');
        return;
      }
      toast.error(getErrorMessage(err, 'Login failed. Check your credentials.'));
    } finally {
      setSubmitting(false);
    }
  };

  const onGoogle = async (credential) => {
    try {
      await googleLogin(credential);
      toast.success('Welcome!');
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(getErrorMessage(err, 'Google sign-in failed.'));
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Log in to your Aether studio."
      footer={
        <>
          New here?{' '}
          <Link to="/signup" className="font-medium text-brand-300 hover:text-brand-200">
            Create an account
          </Link>
        </>
      }
    >
      <SocialLogins onGoogle={onGoogle} />

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <Input
          name="email"
          type="email"
          label="Email"
          placeholder="you@example.com"
          autoComplete="email"
          icon={Mail}
          value={form.email}
          onChange={set('email')}
          error={errors.email}
        />
        <Input
          name="password"
          type="password"
          label="Password"
          placeholder="••••••••"
          autoComplete="current-password"
          icon={Lock}
          value={form.password}
          onChange={set('password')}
          error={errors.password}
        />
        <Button type="submit" className="w-full" loading={submitting} size="lg">
          Log in
        </Button>
      </form>
    </AuthShell>
  );
}
